/**
 * API Configuration
 * 
 * This file contains the main API configuration for the NovaQ Finance System.
 * It handles environment-specific API URLs and common endpoints.
 */

// Environment configuration
const ENV_CONFIG = {
  development: {
    apiUrl: (process.env.REACT_APP_API_URL_DEVELOPMENT || 'http://localhost:9002').trim(),
    timeout: parseInt((process.env.REACT_APP_API_TIMEOUT_DEVELOPMENT || '20000').trim()),
    retryAttempts: parseInt((process.env.REACT_APP_API_RETRY_DEVELOPMENT || '3').trim())
  },
  production: {
    apiUrl: (process.env.REACT_APP_API_URL_PRODUCTION || 'https://novaq.mn').trim(),
    timeout: parseInt((process.env.REACT_APP_API_TIMEOUT_PRODUCTION || '30000').trim()),
    retryAttempts: parseInt((process.env.REACT_APP_API_RETRY_PRODUCTION || '3').trim())
  },
  test: {
    apiUrl: (process.env.REACT_APP_API_URL_DEVELOPMENT || 'http://localhost:9002').trim(),
    timeout: parseInt((process.env.REACT_APP_API_TIMEOUT_DEVELOPMENT || '5000').trim()),
    retryAttempts: parseInt((process.env.REACT_APP_API_RETRY_DEVELOPMENT || '1').trim())
  }
};

// Get current environment
const getCurrentEnv = (): 'development' | 'production' | 'test' => {
  // Check NODE_ENV first for build-time environment detection
  const nodeEnv = process.env.NODE_ENV as 'development' | 'production' | 'test';
  
  // If NODE_ENV is production, use production config
  if (nodeEnv === 'production') {
    return 'production';
  }
  
  // Check if we're in production by looking at the hostname (for runtime detection)
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'novaq.mn' || hostname === 'www.novaq.mn' || hostname.includes('novaq.mn')) {
      return 'production';
    }
  }
  
  return nodeEnv || 'development';
};

// Get API base URL with fallback
// Production дээр бүх API URL-уудыг https://novaq.mn/api гэж ижил нөхцөлтэй харагдуулах
const getApiBaseUrl = (forceProduction: boolean = false): string => {
  // Хэрвээ production орчин бол үргэлж production URL руу чиглүүлнэ
  if (
    forceProduction ||
    process.env.NODE_ENV === 'production' ||
    (typeof window !== 'undefined' &&
      (window.location.hostname === 'novaq.mn' ||
        window.location.hostname === 'www.novaq.mn' ||
        window.location.hostname.includes('novaq.mn')))
  ) {
    // Production дээр үргэлж https://novaq.mn ашиглах
    const productionUrl = (process.env.REACT_APP_API_URL_PRODUCTION || 'https://novaq.mn').trim();
    console.log('🔧 Production mode: using', productionUrl);
    return productionUrl;
  }

  // Development environment-д development URL ашиглах
  const developmentUrl = (process.env.REACT_APP_API_URL_DEVELOPMENT || 'http://localhost:9002').trim();
  console.log('🔧 Development mode: using', developmentUrl);
  return developmentUrl;
}
  
  // Get current environment
 

// API Configuration
export const API_CONFIG = {
  BASE_URL: getApiBaseUrl(),
  TIMEOUT: ENV_CONFIG[getCurrentEnv()].timeout,
  RETRY_ATTEMPTS: ENV_CONFIG[getCurrentEnv()].retryAttempts,
  ENVIRONMENT: getCurrentEnv()
};

// Common API endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/api/auth/login',
    ACCOUNTANT: '/api/auth/accountant',
    SYSADMIN: '/api/auth/sysadmin',
    LOGOUT: '/api/auth/logout'
  },
  
  // User Management
  USERS: {
    BASE: '/api/users',
    ROLES: '/api/roles',
    CHECK_REGISTER: '/api/users/check-register'
  },
  
  // Customer Management
  CUSTOMERS: {
    BASE: '/api/customerslist',
    SAVE_ALL: '/api/customer/save-all',
    DELETE: '/api/deletecustomer',
    BUSINESS_CLASSES: '/api/BusinessClassOid',
    REGIONS: '/api/regions'
  },
  
  // Accountant Management
  ACCOUNTANTS: {
    BASE: '/api/accountant/accountants',
    CHECK_REGISTER: '/api/accountant/accountants/check-register',
    STATS: '/api/customerslist/accountant-stats'
  },
  
  // Tax Management
  TAX: {
    LOGIN: '/api/tax-getlogin',
    LIST: '/api/taxgetlist',
    HISTORY: '/api/taxgethistory',
    CREDENTIALS: '/api/tax-credentials'
  },
  
  // Tax Invoice Management
  TAX_INVOICE: {
    NEGATIVE_BALANCE: (entId: string) => `/api/tax-invoice/negative-balance?entId=${entId}`,
    POSITIVE_BALANCE: (entId: string) => `/api/tax-invoice/positive-balance?entId=${entId}`,
    ALL_BALANCE: (entId: string) => `/api/tax-invoice/all-balance?entId=${entId}`,
    STATS: (entId: string) => `/api/tax-invoice/stats?entId=${entId}`,
    EXPORT: (entId: string) => `/api/tax-invoice/export?entId=${entId}`
  },
  
  // Insurance Management
  INSURANCE: {
    LOGIN: '/api/insurance-login',
    SYNC: '/api/sync-employees'
  },
  
  // Admin
  ADMIN: {
    STATS: '/api/admin-user/admin/stats',
    USERS: '/api/admin/users'
  },
  
  // Reports
  REPORTS: {
    TAX_REPORT: '/api/taxreport',
    RECEIPT: '/api/receipt'
  },
  
  // File Management
  FILES: {
    UPLOAD_CERT: '/api/upload-cert'
  },
  
  // Column Settings
  SETTINGS: {
    COLUMNS: '/api/column-settings'
  }
};

// Legacy export for backward compatibility
export const API_BASE_URL = API_CONFIG.BASE_URL;
export default API_BASE_URL; 